import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, MoreHorizontal, MailPlus, LogOut, Trash2, Search, Check } from 'lucide-react';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Badge } from '@/components/Badge';
import { RoleSelect } from '@/components/RoleSelect';
import { Field } from '@/components/primitives';
import {
  useUpdateProfile,
  useInviteUser,
  useResendInvitation,
  useInvalidateToken,
  useRemoveUser,
  useAddToTenants,
  useUserMembershipOps,
  useTenants,
} from '@/api/hooks';
import type { UserDetail } from '@/api/client';
import { cn } from '@/lib/utils';

export const userName = (u: { firstname: string | null; lastname: string | null; email: string }) =>
  [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email;

// ---------------------------------------------------------------------------
// Kebab menu for user-level actions
// ---------------------------------------------------------------------------
export function UserActionsMenu({
  user,
  isSuperAdmin,
  onRemove,
}: {
  user: UserDetail;
  isSuperAdmin: boolean;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const resend = useResendInvitation(user.id);
  const invalidate = useInvalidateToken(user.id);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const alreadyConfirmed = user.cognitoUserStatus === 'CONFIRMED';
  const pending = resend.isPending || invalidate.isPending;

  const itemCls = (disabled?: boolean) =>
    cn(
      'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs transition-colors',
      disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'
    );

  return (
    <div ref={ref} className="relative">
      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setOpen((o) => !o)} aria-label="User actions">
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 w-56 rounded-lg border bg-popover p-1 shadow-pop animate-scale-in">
          <button
            type="button"
            disabled={alreadyConfirmed || pending}
            className={itemCls(alreadyConfirmed)}
            title={alreadyConfirmed ? 'User is already confirmed' : undefined}
            onClick={async () => {
              try {
                await resend.mutateAsync(user.email);
                toast.success(`Invitation resent to ${user.email}`);
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Resend failed');
              }
              setOpen(false);
            }}
          >
            <MailPlus className="h-3.5 w-3.5 text-muted-foreground" />
            Resend invitation
          </button>
          {isSuperAdmin && (
            <>
              <button
                type="button"
                disabled={pending}
                className={itemCls(pending)}
                onClick={async () => {
                  try {
                    await invalidate.mutateAsync();
                    toast.success('All sessions invalidated — the user must sign in again');
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : 'Failed');
                  }
                  setOpen(false);
                }}
              >
                <LogOut className="h-3.5 w-3.5 text-muted-foreground" />
                Invalidate sessions
              </button>
              <div className="my-1 border-t border-border" />
              <button
                type="button"
                className={cn(itemCls(), 'text-destructive hover:bg-destructive/10')}
                onClick={() => {
                  setOpen(false);
                  onRemove();
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove user…
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit profile attributes
// ---------------------------------------------------------------------------
export function EditUserDialog({ user, open, onClose }: { user: UserDetail | null; open: boolean; onClose: () => void }) {
  const update = useUpdateProfile(user?.id ?? '');
  const [firstname, setFirstname] = useState(user?.firstname ?? '');
  const [lastname, setLastname] = useState(user?.lastname ?? '');
  const [roleDescription, setRoleDescription] = useState(user?.roleDescription ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');

  const handleSave = async () => {
    if (!user) return;
    try {
      await update.mutateAsync({ firstname, lastname, roleDescription, phone });
      toast.success('User updated');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Dialog
      open={open}
      title={`Edit ${user ? userName(user) : ''}`}
      onClose={onClose}
      busy={update.isPending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={update.isPending}>
            {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name">
            <Input value={firstname} onChange={(e) => setFirstname(e.target.value)} autoFocus />
          </Field>
          <Field label="Last name">
            <Input value={lastname} onChange={(e) => setLastname(e.target.value)} />
          </Field>
        </div>
        <Field label="Role description" hint="Free-text role note shown to other admins.">
          <Input value={roleDescription} onChange={(e) => setRoleDescription(e.target.value)} />
        </Field>
        <Field label="Phone">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Global invite (no tenant context)
// ---------------------------------------------------------------------------
export function InviteUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const invite = useInviteUser();
  const [email, setEmail] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [role, setRole] = useState('USER');

  const reset = () => {
    setEmail('');
    setFirstname('');
    setLastname('');
    setRole('USER');
  };

  const handleInvite = async () => {
    try {
      await invite.mutateAsync({ email: email.trim(), firstname: firstname.trim(), lastname: lastname.trim(), tenantRole: role });
      toast.success(`Invitation sent to ${email.trim()}`);
      reset();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Invitation failed');
    }
  };

  return (
    <Dialog
      open={open}
      title="Invite user"
      onClose={() => {
        if (!invite.isPending) {
          reset();
          onClose();
        }
      }}
      busy={invite.isPending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleInvite} disabled={invite.isPending || !email.trim()}>
            {invite.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Send invitation
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Email" hint="They'll receive an invitation email with a temporary password.">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name">
            <Input value={firstname} onChange={(e) => setFirstname(e.target.value)} />
          </Field>
          <Field label="Last name">
            <Input value={lastname} onChange={(e) => setLastname(e.target.value)} />
          </Field>
        </div>
        <Field label="Initial tenant role" hint="Applies when they're added to tenants later.">
          <RoleSelect value={role} onChange={setRole} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add the user to multiple tenants at once
// ---------------------------------------------------------------------------
export function AddToTenantsDialog({
  user,
  open,
  onClose,
}: {
  user: UserDetail | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data: tenants, isLoading } = useTenants();
  const addToTenants = useAddToTenants(user?.id ?? '');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [role, setRole] = useState('USER');

  const existingTenantIds = useMemo(
    () => new Set((user?.tenants.items ?? []).map((m) => m.tenantID)),
    [user]
  );

  const types = useMemo(
    () => [...new Set((tenants ?? []).map((t) => t.typeOfTenantID))].sort(),
    [tenants]
  );

  const available = useMemo(
    () =>
      (tenants ?? [])
        .filter((t) => !existingTenantIds.has(t.id))
        .filter((t) => typeFilter === 'all' || t.typeOfTenantID === typeFilter)
        .filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tenants, existingTenantIds, typeFilter, search]
  );

  // Select-all is scoped to the current filter result: selecting unions the
  // matching ids into the set; deselecting removes only the matching ids.
  const availableIds = useMemo(() => available.map((t) => t.id), [available]);
  const filteredSelectedCount = availableIds.filter((id) => selected.has(id)).length;
  const allFilteredSelected = available.length > 0 && filteredSelectedCount === available.length;
  const someFilteredSelected = filteredSelectedCount > 0 && !allFilteredSelected;

  const toggleAllMatching = () => {
    const next = new Set(selected);
    if (allFilteredSelected) availableIds.forEach((id) => next.delete(id));
    else availableIds.forEach((id) => next.add(id));
    setSelected(next);
  };

  const reset = () => {
    setSearch('');
    setTypeFilter('all');
    setSelected(new Set());
    setRole('USER');
  };

  const handleSubmit = async () => {
    if (!user || selected.size === 0) return;
    const tenantTypes = Object.fromEntries((tenants ?? []).map((t) => [t.id, t.typeOfTenantID]));
    try {
      const { failed } = await addToTenants.mutateAsync({
        tenantIds: [...selected],
        tenantTypes,
        role,
      });
      const n = selected.size - failed.length;
      if (failed.length > 0) {
        toast.error(`${failed.length} of ${selected.size} tenants failed — check permissions`);
      }
      if (n > 0) toast.success(`Added to ${n} tenant${n === 1 ? '' : 's'}`);
      reset();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to add');
    }
  };

  return (
    <Dialog
      open={open}
      title={`Add ${user ? userName(user) : ''} to tenants`}
      onClose={() => {
        if (!addToTenants.isPending) {
          reset();
          onClose();
        }
      }}
      busy={addToTenants.isPending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={addToTenants.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={addToTenants.isPending || selected.size === 0}>
            {addToTenants.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Add to {selected.size || ''} tenant{selected.size === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tenants…" className="pl-8 h-8 text-xs" />
        </div>
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
          <option value="all">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>

        <div className="max-h-48 overflow-auto divide-y divide-border/60 rounded-md border">
          {isLoading ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">Loading tenants…</p>
          ) : available.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              No tenants available{search ? ' for this search' : ''} — the user may already be in all of them.
            </p>
          ) : (
            <>
              <label className="sticky top-0 z-10 flex items-center gap-2.5 px-2.5 py-2 text-xs bg-popover border-b border-border hover:bg-muted/50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-primary"
                  checked={allFilteredSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someFilteredSelected;
                  }}
                  onChange={toggleAllMatching}
                />
                <span className="flex-1 text-muted-foreground">
                  {allFilteredSelected
                    ? `Deselect all ${available.length} matching`
                    : `Select all ${available.length} matching`}
                </span>
                {selected.size > 0 && <span className="text-primary font-medium">{selected.size} selected</span>}
              </label>
              {available.map((t) => (
              <label
                key={t.id}
                className="flex items-center gap-2.5 px-2.5 py-2 text-xs hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-primary"
                  checked={selected.has(t.id)}
                  onChange={() => {
                    const next = new Set(selected);
                    if (next.has(t.id)) next.delete(t.id);
                    else next.add(t.id);
                    setSelected(next);
                  }}
                />
                <span className="flex-1 truncate">{t.name}</span>
                {selected.has(t.id) && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                <Badge variant="secondary" className="shrink-0 text-[9px]">
                  {t.typeOfTenantID}
                </Badge>
              </label>
              ))}
            </>
          )}
        </div>

        <Field label="Role in all selected tenants">
          <RoleSelect value={role} onChange={setRole} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit the role of one membership (user detail context)
// ---------------------------------------------------------------------------
export function EditUserRoleDialog({
  userId,
  membership,
  open,
  onClose,
}: {
  userId: string;
  membership: { id: string; tenantName: string; tenantRole: string | null } | null;
  open: boolean;
  onClose: () => void;
}) {
  const { updateRole } = useUserMembershipOps(userId);
  const [role, setRole] = useState(membership?.tenantRole ?? 'USER');

  const handleSave = async () => {
    if (!membership) return;
    try {
      await updateRole.mutateAsync({ id: membership.id, tenantRole: role });
      toast.success(`Role updated in ${membership.tenantName}`);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Dialog
      open={open}
      title={`Role in ${membership?.tenantName ?? ''}`}
      onClose={onClose}
      busy={updateRole.isPending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={updateRole.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updateRole.isPending}>
            {updateRole.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Save role
          </Button>
        </>
      }
    >
      <Field label="Tenant role">
        <RoleSelect value={role} onChange={setRole} />
      </Field>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Remove user (SuperAdmin): requires typing the email to confirm
// ---------------------------------------------------------------------------
export function RemoveUserDialog({
  user,
  open,
  onClose,
  onRemoved,
}: {
  user: UserDetail | null;
  open: boolean;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const removeUser = useRemoveUser();
  const [confirmEmail, setConfirmEmail] = useState('');

  const matches = user ? confirmEmail.trim().toLowerCase() === user.email.toLowerCase() : false;

  const handleRemove = async () => {
    if (!user || !matches) return;
    try {
      await removeUser.mutateAsync(user.id);
      toast.success(`${user.email} removed`);
      setConfirmEmail('');
      onRemoved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Remove failed');
    }
  };

  return (
    <Dialog
      open={open}
      title={`Remove ${user ? userName(user) : ''}?`}
      onClose={() => {
        if (!removeUser.isPending) {
          setConfirmEmail('');
          onClose();
        }
      }}
      busy={removeUser.isPending}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={removeUser.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={handleRemove} disabled={removeUser.isPending || !matches}>
            {removeUser.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Remove user
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          This permanently removes the Cognito account, profile, and all tenant memberships. Type the user's email to
          confirm:
        </p>
        <p className="text-xs font-medium">{user?.email}</p>
        <Input
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder="Type the email to confirm"
          autoFocus
        />
      </div>
    </Dialog>
  );
}
